/* src/pages/FormBuilder.jsx
 * Form builder - create and edit forms
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc, addDoc, collection, getDocs, query, where, getDocs as getDocsQuery } from 'firebase/firestore';
import {
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { canAccessAIReply } from '../utils/accessControl';
import { isAdmin } from '../utils/admin';
import { FORM_SMS_OPT_IN } from '../utils/flags';
import EmbedSettings from '../components/EmbedSettings';
import { LAYOUT_OPTIONS, getAllLayouts, buildSmartRows } from '../utils/formTemplates';
import FormRenderer from '../components/FormRenderer';
import { useIsMobile } from '../hooks/useIsMobile';
import { useToast } from '../contexts/ToastContext';
import { upsertDefaultSchedulingLink } from '../utils/schedulingLinks';
import { ensureMyWorkspace } from '../utils/ensureMyWorkspace';
import { smsApi, verifyApi, scrapeWebsite, apiPost } from '../utils/api';
import { resolveFromName, domainSenderName } from '../utils/replyIdentity';
import { signatureDisagreesWithFromName, SIGNATURE_MISMATCH_COPY } from '../utils/signatureMismatch';
import { DEFAULT_AI_TEST_MESSAGE, boundKbForTest, groundingGuidance } from '../utils/kbTestPayload';
import { normalizeKbDocument } from '../kb-assistant/kbPromptBudget';
import FieldsTab from '../components/formbuilder/FieldsTab';
import SpamSettingsTab from '../components/formbuilder/SpamSettingsTab';
import DesignTab from '../components/formbuilder/DesignTab';
import TrackingTab from '../components/formbuilder/TrackingTab';
import AiReplyTab from '../components/formbuilder/AiReplyTab';
import KnowledgeTab from '../components/formbuilder/KnowledgeTab';
import { submitSpamTest, formatSpamTestResult } from '../utils/spamFirewallTest';

function generateRowId() {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function slugify(str) {
  if (!str) return '';
  // Strip non-alphanumeric chars (except spaces), then camelCase
  const words = str
    .replace(/[^a-zA-Z0-9 ]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return '';
  return words[0].charAt(0).toLowerCase() + words[0].slice(1).toLowerCase() +
    words.slice(1).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('');
}

function uniqueKey(base, fields, selfId) {
  if (!base) return base;
  const others = fields.filter(f => f.id !== selfId).map(f => f.name);
  if (!others.includes(base)) return base;
  let n = 2;
  while (others.includes(`${base}${n}`)) n++;
  return `${base}${n}`;
}

/** Firestore rejects undefined; strip recursively before any write. */
function stripUndefined(value) {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== 'object') return value;
  if (value instanceof Date) return value;
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefined(item))
      .filter((item) => item !== undefined);
  }
  const out = {};
  for (const [key, val] of Object.entries(value)) {
    if (val === undefined) continue;
    const cleaned = stripUndefined(val);
    if (cleaned !== undefined) {
      out[key] = cleaned;
    }
  }
  return out;
}

export default function FormBuilder() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile } = useAuth();
  const { selectedWorkspaceId, isOwner, workspaceDetails } = useWorkspace();
  const isMobile = useIsMobile();
  const { showToast } = useToast();
  const [form, setForm] = useState(null);
  const [formName, setFormName] = useState('New Form');
  const [fields, setFields] = useState([]);
  const [theme, setTheme] = useState({
    primaryColor: '#171A0D',
    backgroundColor: '#ffffff',
    textColor: '#333333',
    borderColor: '#dddddd',
    borderRadius: '8px',
    buttonColor: '#171A0D',
    buttonTextColor: '#ffffff',
  });
  // Theme editor and form settings are always visible now
  const [showPreview, setShowPreview] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop'); // 'desktop' or 'mobile'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('fields'); // Tab navigation

  // Deep-link support: honour ?tab= query param on initial mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const validTabs = ['fields', 'design', 'knowledge', 'spam-settings', 'tracking', 'ai-reply'];
    if (tab && validTabs.includes(tab)) {
      setActiveTab(tab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount only
  const [showEmbedSettings, setShowEmbedSettings] = useState(false);
  const [embedSettings, setEmbedSettings] = useState({
    width: '100%',
    maxWidth: '600px',
    height: 'auto',
    responsive: true,
    border: true,
    borderRadius: '8px',
    padding: '2rem',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    embedType: 'script',
  });
  const [previewData, setPreviewData] = useState({}); // Form data for preview
  const [formSettings, setFormSettings] = useState({
    businessContext: '',
    businessName: '', // Reply "Shown as" name — never form.name, see replyIdentity.js
    knowledgeBaseId: '',
    spamDetection: true,
    spamRedirect: true,
    whitelist: [], // Email domains or keywords to always allow
    blockList: [], // Block list: domains, email fragments, or keywords; match → spam
    recipients: [], // Notification recipients (empty = workspace owner)
    customSignature: '', // Custom email signature for AI replies
    signatureLogoUrl: '', // Logo URL for email signature
    signatureLinkUrl: '', // Link URL for signature
    signatureLinkText: '', // Link text for signature
    aiReplyDelayMin: 0, // Minimum delay in minutes (0 = instant for testing)
    aiReplyDelayMax: 0, // Maximum delay in minutes (0 = instant for testing)
    followUpDelayDays: 2, // Days between follow-up emails
    maxFollowUps: 2, // Maximum number of follow-up emails
    followUpEnabled: true, // Enable/disable follow-up emails
    pauseAiOnBooking: true, // Auto-pause AI and notify owner when a booking is detected
    autoReplyToSuspicious: false,
  });
  const [workspaceSettings, setWorkspaceSettings] = useState({
    autoBlockRepeatSpam: false,
  });
  const [layout, setLayout] = useState('modern'); // Form layout type
  const [layoutConfig, setLayoutConfig] = useState(null); // Full layout config with styles
  const [rows, setRows] = useState([{ id: 'row-1', fieldIds: [] }]); // Row-based layout: each row has id and ordered fieldIds
  const [designSettings, setDesignSettings] = useState({
    fieldDirection: 'horizontal', // 'horizontal' = fields across in row, 'vertical' = fields stacked
    showScroller: true, // true = allow scroll if form is tall, false = full size no scroll
  });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // Knowledge tab state (website URLs for scrape)
  const [aiWebsiteUrl, setAiWebsiteUrl] = useState('');
  const [aiWebsiteUrls, setAiWebsiteUrls] = useState([]); // Multiple URLs for setup
  const [aiCalendarLink, setAiCalendarLink] = useState('');
  const [aiReplyEnabled, setAiReplyEnabled] = useState(false);
  const [setupingAi, setSetupingAi] = useState(false);
  
  // AI Reply testing state
  const [testLeadName, setTestLeadName] = useState('Sarah Johnson');
  const [testLeadEmail, setTestLeadEmail] = useState('sarah@example.com');
  const [testLeadMessage, setTestLeadMessage] = useState(DEFAULT_AI_TEST_MESSAGE);
  const [testingAiReply, setTestingAiReply] = useState(false);
  const [aiReplyPreview, setAiReplyPreview] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [nextLeadMessage, setNextLeadMessage] = useState('');
  
  // KB Editor state
  const [kbContent, setKbContent] = useState('');
  const [editingKb, setEditingKb] = useState(false);
  const [savingKb, setSavingKb] = useState(false);
  const [workspaceKbs, setWorkspaceKbs] = useState([]);
  const [loadingWorkspaceKbs, setLoadingWorkspaceKbs] = useState(false);
  const [linkingKb, setLinkingKb] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [addingUrl, setAddingUrl] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [testEmailName, setTestEmailName] = useState('Test Lead');
  const [testEmailMessage, setTestEmailMessage] = useState('I am interested in your services and would like to learn more.');
  
  // Permission state
  const [canAccessAIReplyTab, setCanAccessAIReplyTab] = useState(true);
  const [workspaceSmsA2pApproved, setWorkspaceSmsA2pApproved] = useState(false);
  
  // Custom Domain state
  const [availableDomains, setAvailableDomains] = useState([]); // All domains for workspace
  const [selectedDomainId, setSelectedDomainId] = useState(''); // Selected domain ID for this form
  const [customDomain, setCustomDomain] = useState(''); // Legacy - keep for backward compatibility
  const [customDomainFromEmail, setCustomDomainFromEmail] = useState('replies');
  const [customDomainStatus, setCustomDomainStatus] = useState(null);
  const [customDomainDnsRecords, setCustomDomainDnsRecords] = useState(null);
  const [checkingDomainStatus, setCheckingDomainStatus] = useState(false);

  // Reply identity state — explicit sender choice + workspace-level brand fallback
  // (mirrors functions-forms/src/ai-reply/replyIdentity.ts so the preview matches send)
  const [senderMode, setSenderMode] = useState('gocta'); // 'gocta' | 'domain'
  const [workspaceBrand, setWorkspaceBrand] = useState({ businessName: '', name: '' });
  
  // Spam testing state
  const [spamTestResult, setSpamTestResult] = useState(null);
  const [testingSpam, setTestingSpam] = useState(false);
  const [customTestData, setCustomTestData] = useState({
    name: '',
    email: '',
    message: '',
  });

  // Tracking state
  const [trackingPixels, setTrackingPixels] = useState([]);

  useEffect(() => {
    if (currentUser) {
      checkAIReplyAccess();
      if (formId && formId !== 'new') {
        loadForm();
      } else {
        setLoading(false);
        // Initialize empty form for new forms
        setFields([]);
        setRows([{ id: 'row-1', fieldIds: [] }]);
        // Initialize default layout
        const defaultLayout = LAYOUT_OPTIONS.find(l => l.id === 'modern') || LAYOUT_OPTIONS[0];
        setLayout('modern');
        setLayoutConfig(defaultLayout);
      }
    }
  }, [formId, currentUser]);

  async function checkAIReplyAccess() {
    if (!currentUser || !selectedWorkspaceId) return;
    
    // Get the form's workspaceId (or use selected workspace if form not loaded)
    const formWorkspaceId = form?.workspaceId || selectedWorkspaceId;
    
    // Check access to the form's workspace
    const hasAccess = await canAccessAIReply(currentUser.uid, formWorkspaceId);
    setCanAccessAIReplyTab(hasAccess);
    
    // If user doesn't have access and is on AI Reply tab, switch to Fields
    if (!hasAccess && activeTab === 'ai-reply') {
      setActiveTab('fields');
    }
  }
  
  useEffect(() => {
    if (selectedWorkspaceId) {
      checkAIReplyAccess();
    }
  }, [selectedWorkspaceId]);

  useEffect(() => {
    if (!selectedWorkspaceId) {
      setWorkspaceSmsA2pApproved(false);
      return;
    }
    smsApi
      .getSmsStatus(selectedWorkspaceId)
      .then((res) => {
        const sn = res.smsNumber;
        setWorkspaceSmsA2pApproved(sn?.status === 'active' && sn?.a2p?.status === 'approved');
      })
      .catch(() => setWorkspaceSmsA2pApproved(false));
  }, [selectedWorkspaceId]);

  async function loadWorkspaceKbs() {
    const wsId = selectedWorkspaceId || form?.workspaceId;
    if (!wsId) {
      setWorkspaceKbs([]);
      return;
    }
    setLoadingWorkspaceKbs(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'knowledge_bases'), where('workspaceId', '==', wsId))
      );
      const list = [];
      snap.forEach((d) => {
        const data = d.data();
        list.push({
          id: d.id,
          name: data.name || 'Unnamed knowledge base',
          snippet: (data.content || '').slice(0, 80),
        });
      });
      list.sort((a, b) => a.name.localeCompare(b.name));
      setWorkspaceKbs(list);
    } catch (e) {
      console.error('Error loading workspace KBs:', e);
    } finally {
      setLoadingWorkspaceKbs(false);
    }
  }

  useEffect(() => {
    if (selectedWorkspaceId) {
      loadWorkspaceKbs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkspaceId]);

  async function loadForm() {
    try {
      const formDoc = await getDoc(doc(db, 'forms', formId));
      if (formDoc.exists()) {
        const formData = formDoc.data();
        setForm(formData);
        setFormName(formData.name || 'New Form');
        const loadedFields = formData.fields || [];
        setFields(loadedFields);
        // Backward compat: if no rows, derive a smart multi-row layout
        const loadedRows = formData.rows && formData.rows.length > 0
          ? formData.rows
          : buildSmartRows(loadedFields);
        setRows(loadedRows);
        setTheme(formData.theme || {
          primaryColor: '#171A0D',
          backgroundColor: '#ffffff',
          textColor: '#333333',
          borderColor: '#dddddd',
          borderRadius: '8px',
          buttonColor: '#171A0D',
          buttonTextColor: '#ffffff',
        });
        // Load layout (map legacy card to modern for picker + preview)
        const rawLayout = formData.layout || 'modern';
        const formLayout = rawLayout === 'card' ? 'modern' : rawLayout;
        setLayout(formLayout);
        const layoutOption = LAYOUT_OPTIONS.find(l => l.id === formLayout) || LAYOUT_OPTIONS[0];
        setLayoutConfig(layoutOption);
        // Load design settings
        const ds = formData.designSettings || {};
        setDesignSettings({
          fieldDirection: ds.fieldDirection || 'horizontal',
          showScroller: ds.showScroller !== false,
        });
        // Load embed settings if present
        if (formData.embedSettings) {
          setEmbedSettings(prev => ({ ...prev, ...formData.embedSettings }));
        }
        // Load tracking pixels
        setTrackingPixels(formData.trackingPixels || []);
        // Load form settings
        if (formData.settings) {
          setFormSettings({
            businessContext: formData.settings.businessContext || '',
            businessName: formData.settings.businessName || '',
            knowledgeBaseId: formData.settings.knowledgeBaseId || '',
            spamDetection: formData.settings.spamDetection !== false,
            spamRedirect: formData.settings.spamRedirect !== false,
            whitelist: formData.settings.whitelist || [],
            blockList: formData.settings.blockList || [],
            recipients: formData.settings.recipients || [],
            emailPlusAddress: formData.settings.emailPlusAddress || '',
            customSignature: formData.settings.customSignature || '',
            signatureLogoUrl: formData.settings.signatureLogoUrl || '',
            signatureLinkUrl: formData.settings.signatureLinkUrl || '',
            signatureLinkText: formData.settings.signatureLinkText || '',
            aiReplyDelayMin: formData.settings.aiReplyDelayMin || 0,
            aiReplyDelayMax: formData.settings.aiReplyDelayMax || 0,
            followUpDelayDays: formData.settings.followUpDelayDays ?? 2,
            maxFollowUps: formData.settings.maxFollowUps ?? 2,
            followUpEnabled: formData.settings.followUpEnabled !== false, // Default to true
            pauseAiOnBooking: formData.settings.pauseAiOnBooking !== false, // Default to true
            smsOptIn: formData.settings.smsOptIn || null,
            autoReplyToSuspicious: formData.settings.autoReplyToSuspicious === true,
          });
          
          // Load AI reply settings and URL lists (restore websiteUrls so they persist after refresh)
          setAiReplyEnabled(formData.settings.autoReply || false);
          setAiWebsiteUrl(formData.settings.websiteUrl || '');
          setAiWebsiteUrls(formData.settings.websiteUrls || []);

          // Load sender mode — legacy forms (saved before this field existed) default
          // to 'domain' when they already have a selectedDomainId, else 'gocta'.
          setSenderMode(
            formData.settings.senderMode || (formData.settings.selectedDomainId ? 'domain' : 'gocta')
          );

          // Load workspace-level brand fallback (businessName / display name)
          const brandWorkspaceId = selectedWorkspaceId || formData.workspaceId;
          if (brandWorkspaceId) {
            try {
              const workspaceDoc = await getDoc(doc(db, 'workspaces', brandWorkspaceId));
              if (workspaceDoc.exists()) {
                const wsData = workspaceDoc.data();
                setWorkspaceBrand({ businessName: wsData.businessName || '', name: wsData.name || '' });
                setWorkspaceSettings({
                  autoBlockRepeatSpam: wsData.settings?.autoBlockRepeatSpam === true,
                });
              }
            } catch (wsError) {
              console.error('Error loading workspace brand:', wsError);
            }
          }
          
          // Load all available domains for workspace
          if (currentUser) {
            try {
              const domainsRef = collection(db, 'email_domains');
              const domainQuery = query(
                domainsRef,
                where('workspaceId', '==', selectedWorkspaceId || formData.workspaceId)
              );
              const domainSnapshot = await getDocs(domainQuery);
              
              const domainsList = [];
              domainSnapshot.forEach((doc) => {
                domainsList.push({ id: doc.id, ...doc.data() });
              });
              
              // Sort: verified first
              domainsList.sort((a, b) => {
                if (a.verificationStatus === 'verified' && b.verificationStatus !== 'verified') return -1;
                if (a.verificationStatus !== 'verified' && b.verificationStatus === 'verified') return 1;
                return 0;
              });
              
              setAvailableDomains(domainsList);
              
              // Set selected domain from form settings or first verified domain
              const formSelectedDomainId = formData.settings?.selectedDomainId;
              if (formSelectedDomainId) {
                const found = domainsList.find(d => d.id === formSelectedDomainId);
                if (found) {
                  setSelectedDomainId(formSelectedDomainId);
                  setCustomDomain(found.domain);
                  setCustomDomainFromEmail(found.fromEmail.split('@')[0]);
                  setCustomDomainStatus({ verified: found.verificationStatus === 'verified', status: found.verificationStatus });
                  setCustomDomainDnsRecords(found.dnsRecords);
                }
              } else if (domainsList.length > 0) {
                // Default to first verified domain, or first domain if none verified
                const defaultDomain = domainsList.find(d => d.verificationStatus === 'verified') || domainsList[0];
                setSelectedDomainId(defaultDomain.id);
                setCustomDomain(defaultDomain.domain);
                setCustomDomainFromEmail(defaultDomain.fromEmail.split('@')[0]);
                setCustomDomainStatus({ verified: defaultDomain.verificationStatus === 'verified', status: defaultDomain.verificationStatus });
                setCustomDomainDnsRecords(defaultDomain.dnsRecords);
              }
            } catch (error) {
              console.error('Error loading domains:', error);
            }
          }
          
          // Load knowledge base content
          if (formData.settings.knowledgeBaseId) {
            const kbDoc = await getDoc(doc(db, 'knowledge_bases', formData.settings.knowledgeBaseId));
            if (kbDoc.exists()) {
              const kbData = kbDoc.data();
              setKbContent(kbData.content || '');
            }
            
            // Load calendar link if exists
            const linksSnapshot = await getDocs(query(collection(db, 'scheduling_links'), 
              where('workspaceId', '==', selectedWorkspaceId || formData.workspaceId)));
            if (!linksSnapshot.empty) {
              const firstLink = linksSnapshot.docs[0].data();
              setAiCalendarLink(firstLink.url || '');
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading form:', error);
      showToast('Failed to load form', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function saveKbContent(nextContent) {
    if (!formSettings.knowledgeBaseId) return;
    const content = normalizeKbDocument(nextContent);

    setSavingKb(true);
    try {
      await updateDoc(doc(db, 'knowledge_bases', formSettings.knowledgeBaseId), stripUndefined({
        content,
        updatedAt: new Date(),
      }));

      setKbContent(content);
      setFormSettings((prev) => ({ ...prev, businessContext: content }));

      if (formId) {
        const existingSettings = form?.settings || {};
        await updateDoc(doc(db, 'forms', formId), stripUndefined({
          settings: {
            ...existingSettings,
            businessContext: content,
          },
        }));
      }

      showToast('Knowledge base updated! AI and spam detection will use the new content.');
    } catch (error) {
      console.error('[KB SAVE] Error:', error);
      showToast('Error saving KB: ' + error.message, 'error');
      throw error;
    } finally {
      setSavingKb(false);
    }
  }

  async function saveKbEdits() {
    try {
      await saveKbContent(kbContent);
      setEditingKb(false);
    } catch {
      // saveKbContent already toasts
    }
  }

  async function addUrlToKb() {
    if (!formSettings.knowledgeBaseId || !newUrl) {
      showToast('Please enter a URL to add', 'info');
      return;
    }

    setAddingUrl(true);
    try {
      console.log('[ADD URL] Scraping:', newUrl);

      const result = await scrapeWebsite(newUrl, {
        fullContent: true,
        workspaceId: selectedWorkspaceId || form?.workspaceId,
      });
      const newContent = result.businessContext;
      
      console.log('[ADD URL] Scraped', newContent.length, 'chars');
      
      // Append to existing KB
      const updatedContent = kbContent + '\n\n---\n\n' + newContent;
      
      // Save to Firestore
      const kbDoc = await getDoc(doc(db, 'knowledge_bases', formSettings.knowledgeBaseId));
      const kbData = kbDoc.data();
      const existingSources = kbData?.sources || [];
      
      await updateDoc(doc(db, 'knowledge_bases', formSettings.knowledgeBaseId), stripUndefined({
        content: updatedContent,
        sources: [...existingSources, { type: 'website', url: newUrl, addedAt: new Date() }],
        updatedAt: new Date(),
      }));
      
      // Update local state
      setKbContent(updatedContent);
      setFormSettings((prev) => ({ ...prev, businessContext: updatedContent }));
      setNewUrl('');

      if (formId) {
        const existingSettings = form?.settings || {};
        await updateDoc(doc(db, 'forms', formId), stripUndefined({
          settings: {
            ...existingSettings,
            businessContext: updatedContent,
          },
        }));
      }
      
      showToast(`Added ${newContent.length.toLocaleString()} more characters to KB! Total: ${updatedContent.length.toLocaleString()} chars`);
      
    } catch (error) {
      console.error('[ADD URL] Error:', error);
      showToast('Error adding URL: ' + error.message, 'error');
    } finally {
      setAddingUrl(false);
    }
  }

  async function testSpamSubmission(testType) {
    if (!formId) {
      showToast('Please save the form first', 'info');
      return;
    }

    setTestingSpam(true);
    setSpamTestResult(null);

    try {
      const raw = await submitSpamTest({
        formId,
        testType,
        customData: customTestData,
      });
      const formatted = formatSpamTestResult(raw, {
        ...formSettings,
        autoReply: aiReplyEnabled,
      }, workspaceSettings);
      setSpamTestResult(formatted);
    } catch (error) {
      console.error('Test error:', error);
      setSpamTestResult({
        success: false,
        error: error.message,
        testType,
      });
    } finally {
      setTestingSpam(false);
    }
  }

  async function sendRealTestEmail() {
    if (!formSettings.knowledgeBaseId) {
      showToast('Please set up a knowledge base in the Knowledge tab first', 'info');
      return;
    }

    setSendingTestEmail(true);
    try {
      console.log('[SEND TEST EMAIL] Calling test email endpoint...');
      
      const emailTo = testEmailRecipient || currentUser.email;

      const result = await apiPost('/sendTestEmail', {
        formId: formId,
        recipientEmail: emailTo,
        leadName: testEmailName || 'Test Lead',
        leadMessage: testEmailMessage || 'I am interested in your services and would like to learn more.',
      });
      
      if (result.success) {
        showToast(`Test email sent to ${emailTo}! Check your inbox in 30-60 seconds. From: ${formName} <replies@gocta.ai>`);
      } else {
        showToast('Email queued but may take up to 5 minutes to arrive.', 'info');
      }
      
    } catch (error) {
      console.error('[SEND TEST EMAIL] Error:', error);
      showToast('Error sending test email: ' + error.message, 'error');
    } finally {
      setSendingTestEmail(false);
    }
  }

  async function sendTestEmail() {
    if (conversationHistory.length === 0) {
      showToast('Please test a conversation first', 'info');
      return;
    }

    // Download conversation as text file
    try {
      let emailBody = 'Test AI Conversation:\n\n';
      emailBody += '='.repeat(50) + '\n\n';
      
      conversationHistory.forEach((turn, i) => {
        emailBody += `--- Turn ${i + 1} ---\n\n`;
        emailBody += `💬 Lead: ${turn.leadMessage}\n\n`;
        emailBody += `🤖 AI Reply:\n${turn.aiReply}\n\n`;
        emailBody += '-'.repeat(50) + '\n\n';
      });
      
      emailBody += `This is what leads will receive when they submit to "${formName}".\n\n`;
      emailBody += `Test conducted at: ${new Date().toLocaleString()}`;
      
      const blob = new Blob([emailBody], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-conversation-test-${Date.now()}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      
      showToast('Conversation downloaded!');
      
    } catch (error) {
      console.error('[DOWNLOAD CONVERSATION] Error:', error);
      showToast('Error: ' + error.message, 'error');
    }
  }

  async function testAIReply() {
    if (!formSettings.knowledgeBaseId) {
      showToast('Please set up a knowledge base in the Knowledge tab first', 'info');
      return;
    }

    setTestingAiReply(true);
    try {
      console.log('[TEST AI REPLY] Generating REAL AI reply using KB...');

      let kbText = (kbContent && kbContent.trim()) ? kbContent : '';
      if (!kbText) {
        const kbDoc = await getDoc(doc(db, 'knowledge_bases', formSettings.knowledgeBaseId));
        if (!kbDoc.exists()) {
          throw new Error('Knowledge base not found');
        }
        kbText = kbDoc.data().content || '';
      }

      const knowledgeBase = boundKbForTest(kbText);
      console.log('[TEST AI REPLY] KB loaded, content length:', kbText.length, 'sent:', knowledgeBase.length);

      const selectedDomainObj = availableDomains.find((d) => d.id === selectedDomainId);
      const domainVerified = selectedDomainObj?.verificationStatus === 'verified';
      const usingDomain = senderMode === 'domain' && !!selectedDomainObj && domainVerified;

      const aiResult = await verifyApi.testAiReply({
        leadName: testLeadName,
        leadEmail: testLeadEmail,
        leadMessage: testLeadMessage,
        knowledgeBase,
        calendarLink: aiCalendarLink,
        conversationHistory: conversationHistory,
        formBusinessName: formSettings.businessName,
        workspaceBusinessName: workspaceBrand.businessName,
        workspaceName: workspaceBrand.name,
        domainFromName: usingDomain ? domainSenderName(selectedDomainObj) : undefined,
        workspaceId: selectedWorkspaceId || form?.workspaceId,
      });
      
      if (!aiResult.success) {
        throw new Error(aiResult.error || 'AI failed to generate reply');
      }
      
      console.log('[TEST AI REPLY] ✅ AI generated reply successfully');

      const body = aiResult.body;
      
      const preview = {
        subject: aiResult.subject,
        body: body,
        from: `${formName} <replies@gocta.ai>`,
        replyTo: currentUser?.email || 'noreply@example.com',
      };
      
      setAiReplyPreview(preview);
      
      // Add to conversation history
      setConversationHistory([
        ...conversationHistory,
        {
          turn: conversationHistory.length + 1,
          leadMessage: testLeadMessage,
          aiReply: body,
          groundingNote: groundingGuidance({
            leadMessage: testLeadMessage,
            aiReply: body,
            knowledgeBase,
          }),
        }
      ]);
      
      setTestLeadMessage(''); // Clear for next turn
      
    } catch (error) {
      console.error('[TEST AI REPLY] Error:', error);
      showToast('Error testing AI reply: ' + error.message, 'error');
    } finally {
      setTestingAiReply(false);
    }
  }

  async function linkExistingKb(kbId) {
    if (!kbId || kbId === formSettings.knowledgeBaseId || !formId) return;

    setLinkingKb(true);
    try {
      const kbDoc = await getDoc(doc(db, 'knowledge_bases', kbId));
      if (!kbDoc.exists()) {
        showToast('Knowledge base not found', 'error');
        return;
      }
      const content = kbDoc.data().content || '';

      setFormSettings((prev) => ({
        ...prev,
        knowledgeBaseId: kbId,
        businessContext: content,
      }));
      setKbContent(content);
      setEditingKb(false);

      const existingSettings = form?.settings || {};
      await updateDoc(doc(db, 'forms', formId), stripUndefined({
        settings: {
          ...existingSettings,
          knowledgeBaseId: kbId,
          businessContext: content,
        },
      }));

      showToast('Knowledge base linked');
    } catch (error) {
      console.error('[LINK KB] Error:', error);
      showToast('Error linking knowledge base: ' + error.message, 'error');
    } finally {
      setLinkingKb(false);
    }
  }

  async function setupKnowledgeFromUrls() {
    // Get all URLs (from array or single input)
    const urlsToScrape = aiWebsiteUrls.length > 0 
      ? aiWebsiteUrls 
      : aiWebsiteUrl ? [aiWebsiteUrl] : [];
    
    if (urlsToScrape.length === 0) {
      showToast('Please add at least one URL', 'info');
      return;
    }

    setSetupingAi(true);
    try {
      console.log('[KNOWLEDGE SETUP] Scraping', urlsToScrape.length, 'URLs...');
      
      let combinedContent = '';
      const sources = [];
      
      // Scrape each URL
      for (let i = 0; i < urlsToScrape.length; i++) {
        const url = urlsToScrape[i];
        console.log(`[KNOWLEDGE SETUP] Scraping ${i + 1}/${urlsToScrape.length}:`, url);

        let result;
        try {
          result = await scrapeWebsite(url, {
            fullContent: true,
            workspaceId: selectedWorkspaceId || form?.workspaceId,
          });
        } catch (scrapeErr) {
          console.warn(`Failed to scrape ${url}, skipping`, scrapeErr);
          continue;
        }

        const content = result.businessContext;
        
        console.log(`Scraped ${content.length} chars from ${url}`);
        
        // Append with separator
        if (combinedContent) combinedContent += '\n\n---\n\n';
        combinedContent += `[From: ${url}]\n\n${content}`;
        
        sources.push({ type: 'website', url: url, addedAt: new Date() });
      }
      
      console.log('[KNOWLEDGE SETUP] Total:', combinedContent.length, 'chars');
      
      // Create KB with combined content from all URLs
      const kbData = {
        workspaceId: selectedWorkspaceId || form?.workspaceId || currentUser.uid,
        name: `KB for ${formName}`,
        content: combinedContent,
        isShared: false,
        formsUsing: [formId],
        sources: sources, // All scraped URLs
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      
      const kbRef = await addDoc(collection(db, 'knowledge_bases'), stripUndefined(kbData));
      
      // Update form settings (KB + spam businessContext; no autoReply)
      setFormSettings((prev) => ({
        ...prev,
        knowledgeBaseId: kbRef.id,
        businessContext: combinedContent,
      }));
      
      // Update local state with new KB content
      setKbContent(combinedContent);
      
      // AUTO-SAVE after setup
      console.log('[KNOWLEDGE SETUP] Auto-saving form...');
      if (formId) {
        const existingSettings = form?.settings || {};
        await updateDoc(doc(db, 'forms', formId), stripUndefined({
          settings: {
            ...existingSettings,
            knowledgeBaseId: kbRef.id,
            businessContext: combinedContent,
            websiteUrls: urlsToScrape, // Save all URLs
          },
        }));
        console.log('[KNOWLEDGE SETUP] Form saved automatically');
      }
      
      showToast(`Knowledge base built! Scraped ${urlsToScrape.length} page(s), ${combinedContent.length.toLocaleString()} characters. Used for spam detection and AI replies.`);
      await loadWorkspaceKbs();
      
    } catch (error) {
      console.error('[KNOWLEDGE SETUP] Error:', error);
      showToast('Error: ' + error.message, 'error');
    } finally {
      setSetupingAi(false);
    }
  }

  async function saveForm() {
    if (!formId) {
      showToast('Form ID not found', 'error');
      return;
    }

    if (aiReplyEnabled && !formSettings.knowledgeBaseId) {
      setActiveTab('knowledge');
      showToast('AI replies need a knowledge base. Set it up in the Knowledge tab, then save.', 'error');
      return;
    }

    const selectedDomainObj = availableDomains.find((d) => d.id === selectedDomainId);
    const domainVerified = selectedDomainObj?.verificationStatus === 'verified';
    const usingDomain = senderMode === 'domain' && !!selectedDomainObj && domainVerified;
    const fromNameForSave = resolveFromName({
      formBusinessName: formSettings.businessName,
      workspaceBusinessName: workspaceBrand.businessName,
      workspaceName: workspaceBrand.name,
      domainFromName: usingDomain ? domainSenderName(selectedDomainObj) : undefined,
    });
    if (signatureDisagreesWithFromName(fromNameForSave, formSettings.customSignature)) {
      showToast(SIGNATURE_MISMATCH_COPY, 'info');
    }

    setSaving(true);
    try {
      if (aiCalendarLink) {
        await upsertDefaultSchedulingLink({
          workspaceId: form?.workspaceId || selectedWorkspaceId || currentUser.uid,
          url: aiCalendarLink,
          assignedTo: currentUser.email || '',
        });
      }

      // Get existing settings or create new
      const existingSettings = form?.settings || {};
      const updatedSettings = {
        ...existingSettings,
        businessContext: formSettings.businessContext,
        businessName: formSettings.businessName?.trim() || null, // Reply "Shown as" name — never form.name
        senderMode, // 'gocta' | 'domain' — explicit choice, honored unconditionally on send
        spamDetection: formSettings.spamDetection,
        spamRedirect: formSettings.spamRedirect,
        whitelist: formSettings.whitelist || [], // Whitelist for spam overrides
        blockList: formSettings.blockList || [], // Block list: match → spam
        recipients: formSettings.recipients || [], // Notification recipients
        // AI Reply settings
        knowledgeBaseId: formSettings.knowledgeBaseId || null,
        autoReply: aiReplyEnabled,
        aiReplyDelay: 5,
        websiteUrl: aiWebsiteUrl || existingSettings.websiteUrl || null, // Persist website URL
        websiteUrls: aiWebsiteUrls.length ? aiWebsiteUrls : (aiWebsiteUrl ? [aiWebsiteUrl] : (existingSettings.websiteUrls || [])), // Persist URL list
        customSignature: formSettings.customSignature || null, // Custom signature for AI replies
        signatureLogoUrl: formSettings.signatureLogoUrl || null, // Logo URL for signature
        signatureLinkUrl: formSettings.signatureLinkUrl || null, // Link URL for signature
        signatureLinkText: formSettings.signatureLinkText || null, // Link text for signature
        aiReplyDelayMin: formSettings.aiReplyDelayMin || 0, // Minimum delay for AI replies
        aiReplyDelayMax: formSettings.aiReplyDelayMax || 0, // Maximum delay for AI replies
        followUpDelayDays: formSettings.followUpDelayDays ?? 2, // Days between follow-up emails
        maxFollowUps: formSettings.maxFollowUps ?? 2, // Maximum number of follow-up emails
        followUpEnabled: formSettings.followUpEnabled !== false, // Enable/disable follow-up emails
        pauseAiOnBooking: formSettings.pauseAiOnBooking !== false,
        selectedDomainId: selectedDomainId || null, // Selected custom domain for this form
        smsOptIn: formSettings.smsOptIn || null,
        autoReplyToSuspicious: formSettings.autoReplyToSuspicious === true,
      };

      // Form doc is source of truth for ownership. Side-write landed 37a3cef (Jul 21):
      // updateDoc(workspaces/{uid}) on a missing doc hits owner-only rules → permission-denied
      // and blocked the forms update. Never let workspace settings block form save.
      const workspaceId = form?.workspaceId || selectedWorkspaceId || currentUser.uid;
      const ownsTarget = workspaceId === selectedWorkspaceId
        ? isOwner
        : workspaceDetails.find((d) => d.id === workspaceId)?.role === 'owner';
      if (ownsTarget) {
        try {
          // Legacy account workspace only — ensure doc exists before owner-only side-write.
          if (workspaceId === currentUser.uid) await ensureMyWorkspace(currentUser);
          await updateDoc(doc(db, 'workspaces', workspaceId), stripUndefined({
            settings: {
              autoBlockRepeatSpam: workspaceSettings.autoBlockRepeatSpam === true,
            },
          }));
        } catch (wsError) {
          console.warn('[SAVE FORM] Workspace settings write skipped:', wsError);
        }
      }

      await updateDoc(doc(db, 'forms', formId), stripUndefined({
        name: formName,
        fields: fields,
        rows: rows.map((r) => ({
          ...r,
          title: r.title?.trim() || null,
        })),
        theme: theme,
        layout: layout, // Save layout
        designSettings: designSettings,
        settings: updatedSettings,
        trackingPixels: trackingPixels, // Save tracking pixels
        updatedAt: new Date(),
      }));
      showToast('Form saved!');
    } catch (error) {
      console.error('Error saving form:', error);
      showToast('Failed to save form', 'error');
    } finally {
      setSaving(false);
    }
  }

  function addPhoneField() {
    const newField = {
      id: Date.now().toString(),
      type: 'phone',
      label: 'Phone',
      name: 'phone',
      nameAuto: false,
      required: false,
      order: fields.length,
    };
    setFields([...fields, newField]);
    const lastRow = rows.length > 0 ? rows[rows.length - 1] : { id: `row-${Date.now()}`, fieldIds: [] };
    const newRows = rows.length > 0
      ? [...rows.slice(0, -1), { ...lastRow, fieldIds: [...(lastRow.fieldIds || []), newField.id] }]
      : [{ id: lastRow.id, fieldIds: [newField.id] }];
    setRows(newRows);
  }

  function addField(type) {
    const label = `New ${type} field`;
    const newField = {
      id: Date.now().toString(),
      type: type,
      label,
      name: uniqueKey(slugify(label), fields, null),
      nameAuto: true,
      required: false,
      order: fields.length,
    };

    if (type === 'select' || type === 'radio' || type === 'checkbox') {
      newField.options = ['Option 1', 'Option 2'];
    }

    setFields([...fields, newField]);
    // Append to last row (or only row)
    const lastRow = rows.length > 0 ? rows[rows.length - 1] : { id: `row-${Date.now()}`, fieldIds: [] };
    const newRows = rows.length > 0
      ? [...rows.slice(0, -1), { ...lastRow, fieldIds: [...(lastRow.fieldIds || []), newField.id] }]
      : [{ id: lastRow.id, fieldIds: [newField.id] }];
    setRows(newRows);
  }

  function updateField(fieldId, updates) {
    setFields(fields.map(f => {
      if (f.id !== fieldId) return f;
      const merged = { ...f, ...updates };
      // If the user directly edited the key, lock auto-sync off
      if (updates.name !== undefined) {
        merged.nameAuto = false;
      }
      // If the label changed and auto-sync is still on, derive the key from the new label
      if (updates.label !== undefined && merged.nameAuto !== false) {
        merged.name = uniqueKey(slugify(updates.label), fields, fieldId);
      }
      return merged;
    }));
  }

  function deleteField(fieldId) {
    setFields(fields.filter(f => f.id !== fieldId));
    setRows(rows.map(r => ({ ...r, fieldIds: (r.fieldIds || []).filter(id => id !== fieldId) })));
  }

  function addRow() {
    setRows([...rows, { id: generateRowId(), fieldIds: [] }]);
  }

  function removeRow(rowId) {
    if (rows.length <= 1) return; // keep at least one row
    const row = rows.find(r => r.id === rowId);
    if (row && row.fieldIds && row.fieldIds.length > 0) return; // only remove empty rows
    setRows(rows.filter(r => r.id !== rowId));
  }

  function moveFieldToRow(fieldId, targetRowId, insertIndex) {
    const sourceRowIndex = rows.findIndex(r => (r.fieldIds || []).includes(fieldId));
    if (sourceRowIndex < 0) return;
    const targetRowIndex = rows.findIndex(r => r.id === targetRowId);
    if (targetRowIndex < 0) return;
    const sourceRow = rows[sourceRowIndex];
    const targetRow = rows[targetRowIndex];
    const sourceIds = (sourceRow.fieldIds || []).filter(id => id !== fieldId);
    let targetIds = [...(targetRow.fieldIds || [])];
    if (sourceRowIndex === targetRowIndex) {
      targetIds = sourceIds;
      const at = insertIndex != null ? insertIndex : targetIds.length;
      targetIds.splice(at, 0, fieldId);
    } else {
      const at = insertIndex != null ? Math.min(insertIndex, targetIds.length) : targetIds.length;
      targetIds.splice(at, 0, fieldId);
    }
    const newRows = rows.map((r, i) => {
      if (i === sourceRowIndex) return { ...r, fieldIds: sourceIds };
      if (i === targetRowIndex) return { ...r, fieldIds: targetIds };
      return r;
    });
    setRows(newRows);
  }

  function updateRowTitle(rowId, title) {
    setRows(rows.map(r => (
      r.id === rowId ? { ...r, title: title === '' ? undefined : title } : r
    )));
  }

  function reorderFieldInRow(rowId, fieldId, newIndex) {
    const rowIndex = rows.findIndex(r => r.id === rowId);
    if (rowIndex < 0) return;
    const row = rows[rowIndex];
    const ids = [...(row.fieldIds || [])];
    const from = ids.indexOf(fieldId);
    if (from < 0) return;
    ids.splice(from, 1);
    ids.splice(newIndex, 0, fieldId);
    setRows(rows.map((r, i) => (i === rowIndex ? { ...r, fieldIds: ids } : r)));
  }

  function getFormUrl() {
    return `${window.location.origin}/form/${formId}`;
  }

  if (loading) {
    return <div className="fb-page fb-loading">Loading form...</div>;
  }

  return (
    <div className="fb-page">
      <header className={`fb-header${isMobile ? ' is-mobile' : ''}`}>
        <div className={`fb-header-left${isMobile ? ' is-mobile' : ''}`}>
          <input
            type="text"
            className={`fb-form-name${isMobile ? ' is-mobile' : ''}`}
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
          />
        </div>
        <div className="fb-header-actions">
          <button
            type="button"
            className="inbox-pill-btn-primary"
            onClick={saveForm}
            disabled={saving}
            style={{ minHeight: isMobile ? 44 : undefined, opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
          >
            {saving ? 'Saving...' : 'Save Form'}
          </button>
          {formId && (
            <>
              <button
                type="button"
                className="inbox-pill-btn"
                onClick={() => setShowEmbedSettings(true)}
                style={{ minHeight: isMobile ? 44 : undefined }}
              >
                Embed Code
              </button>
              <button
                type="button"
                className="inbox-pill-btn-primary"
                onClick={() => setShowPreview(true)}
                style={{ minHeight: isMobile ? 44 : undefined }}
              >
                Preview
              </button>
            </>
          )}
        </div>
      </header>

      {/* Tab Navigation - on mobile: horizontal scroll so all tabs fit */}
      <div className="fb-tab-bar">
        <div className={`fb-tab-bar-inner${isMobile ? ' is-mobile' : ''}`}>
          {[
            { id: 'fields', label: 'Fields' },
            { id: 'design', label: 'Design' },
            { id: 'knowledge', label: 'Knowledge Base' },
            { id: 'spam-settings', label: 'Firewall' },
            { id: 'tracking', label: 'Tracking' },
            ...(canAccessAIReplyTab ? [{ id: 'ai-reply', label: 'Operator' }] : [])
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`fb-tab-btn${isMobile ? ' is-mobile' : ''}${activeTab === tab.id ? ' active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <main className={`fb-main${isMobile ? ' is-mobile' : ''}`}>
        {/* FIELDS TAB */}
        {activeTab === 'fields' && (
          <FieldsTab
            isMobile={isMobile}
            fields={fields}
            rows={rows}
            sensors={sensors}
            addField={addField}
            addRow={addRow}
            removeRow={removeRow}
            moveFieldToRow={moveFieldToRow}
            updateRowTitle={updateRowTitle}
            updateField={updateField}
            deleteField={deleteField}
          />
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'spam-settings' && (
          <SpamSettingsTab
            isMobile={isMobile}
            formSettings={formSettings}
            setFormSettings={setFormSettings}
            workspaceSettings={workspaceSettings}
            setWorkspaceSettings={setWorkspaceSettings}
            aiReplyEnabled={aiReplyEnabled}
            currentUser={currentUser}
            showToast={showToast}
            testSpamSubmission={testSpamSubmission}
            testingSpam={testingSpam}
            spamTestResult={spamTestResult}
            customTestData={customTestData}
            setCustomTestData={setCustomTestData}
            onOpenKnowledgeTab={() => setActiveTab('knowledge')}
          />
        )}

        {/* DESIGN TAB */}
        {activeTab === 'design' && (
          <DesignTab
            isMobile={isMobile}
            layout={layout}
            setLayout={setLayout}
            layoutConfig={layoutConfig}
            setLayoutConfig={setLayoutConfig}
            theme={theme}
            setTheme={setTheme}
            designSettings={designSettings}
            setDesignSettings={setDesignSettings}
            formName={formName}
            fields={fields}
            rows={rows}
            previewData={previewData}
            setPreviewData={setPreviewData}
            showToast={showToast}
          />
        )}

        {/* KNOWLEDGE TAB */}
        {activeTab === 'knowledge' && (
          <KnowledgeTab
            isMobile={isMobile}
            formSettings={formSettings}
            setFormSettings={setFormSettings}
            aiWebsiteUrl={aiWebsiteUrl}
            setAiWebsiteUrl={setAiWebsiteUrl}
            aiWebsiteUrls={aiWebsiteUrls}
            setAiWebsiteUrls={setAiWebsiteUrls}
            setupingAi={setupingAi}
            kbContent={kbContent}
            setKbContent={setKbContent}
            editingKb={editingKb}
            setEditingKb={setEditingKb}
            savingKb={savingKb}
            newUrl={newUrl}
            setNewUrl={setNewUrl}
            addingUrl={addingUrl}
            aiReplyEnabled={aiReplyEnabled}
            setAiReplyEnabled={setAiReplyEnabled}
            aiCalendarLink={aiCalendarLink}
            setAiCalendarLink={setAiCalendarLink}
            setupKnowledgeFromUrls={setupKnowledgeFromUrls}
            saveKbEdits={saveKbEdits}
            applyKbDraft={saveKbContent}
            selectedWorkspaceId={selectedWorkspaceId}
            addUrlToKb={addUrlToKb}
            loadForm={loadForm}
            workspaceKbs={workspaceKbs}
            loadingWorkspaceKbs={loadingWorkspaceKbs}
            linkingKb={linkingKb}
            linkExistingKb={linkExistingKb}
            testLeadName={testLeadName}
            setTestLeadName={setTestLeadName}
            testLeadEmail={testLeadEmail}
            setTestLeadEmail={setTestLeadEmail}
            testLeadMessage={testLeadMessage}
            setTestLeadMessage={setTestLeadMessage}
            conversationHistory={conversationHistory}
            setConversationHistory={setConversationHistory}
            setAiReplyPreview={setAiReplyPreview}
            testingAiReply={testingAiReply}
            testAIReply={testAIReply}
            sendTestEmail={sendTestEmail}
          />
        )}

        {/* TRACKING TAB */}
        {activeTab === 'tracking' && (
          <TrackingTab
            isMobile={isMobile}
            formId={formId}
            formName={formName}
            trackingPixels={trackingPixels}
            trackingVerification={form?.trackingVerification ?? {}}
            setTrackingPixels={setTrackingPixels}
            showToast={showToast}
          />
        )}

        {/* AI REPLY TAB */}
        {activeTab === 'ai-reply' && (
          <AiReplyTab
            canAccessAIReplyTab={canAccessAIReplyTab}
            formSettings={formSettings}
            setFormSettings={setFormSettings}
            formName={formName}
            formId={formId}
            form={form}
            currentUser={currentUser}
            selectedWorkspaceId={selectedWorkspaceId}
            workspaceBrand={workspaceBrand}
            senderMode={senderMode}
            setSenderMode={setSenderMode}
            availableDomains={availableDomains}
            setAvailableDomains={setAvailableDomains}
            selectedDomainId={selectedDomainId}
            setSelectedDomainId={setSelectedDomainId}
            customDomain={customDomain}
            setCustomDomain={setCustomDomain}
            customDomainFromEmail={customDomainFromEmail}
            setCustomDomainFromEmail={setCustomDomainFromEmail}
            customDomainStatus={customDomainStatus}
            setCustomDomainStatus={setCustomDomainStatus}
            customDomainDnsRecords={customDomainDnsRecords}
            setCustomDomainDnsRecords={setCustomDomainDnsRecords}
            aiCalendarLink={aiCalendarLink}
            setAiCalendarLink={setAiCalendarLink}
            aiReplyEnabled={aiReplyEnabled}
            setAiReplyEnabled={setAiReplyEnabled}
            testLeadName={testLeadName}
            setTestLeadName={setTestLeadName}
            testLeadEmail={testLeadEmail}
            setTestLeadEmail={setTestLeadEmail}
            testLeadMessage={testLeadMessage}
            setTestLeadMessage={setTestLeadMessage}
            testingAiReply={testingAiReply}
            aiReplyPreview={aiReplyPreview}
            setAiReplyPreview={setAiReplyPreview}
            conversationHistory={conversationHistory}
            setConversationHistory={setConversationHistory}
            sendingTestEmail={sendingTestEmail}
            testEmailRecipient={testEmailRecipient}
            setTestEmailRecipient={setTestEmailRecipient}
            testEmailName={testEmailName}
            setTestEmailName={setTestEmailName}
            testEmailMessage={testEmailMessage}
            setTestEmailMessage={setTestEmailMessage}
            showToast={showToast}
            navigate={navigate}
            onOpenKnowledgeTab={() => setActiveTab('knowledge')}
            testAIReply={testAIReply}
            sendTestEmail={sendTestEmail}
            sendRealTestEmail={sendRealTestEmail}
            showSmsOptInBuilder={FORM_SMS_OPT_IN || isAdmin(currentUser, userProfile)}
            workspaceSmsA2pApproved={workspaceSmsA2pApproved}
            fields={fields}
            onAddPhoneField={addPhoneField}
          />
        )}

      </main>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fb-modal-overlay">
          <div className={`fb-modal ${previewMode === 'mobile' ? 'preview-mobile' : 'preview-desktop'}`}>
            <div className="fb-modal-header">
              <h2 className="fb-modal-title">Form Preview</h2>
              <div className="fb-modal-header-actions">
                <div className="fb-segmented">
                  <button
                    type="button"
                    className={`fb-segmented-btn${previewMode === 'desktop' ? ' active' : ''}`}
                    onClick={() => setPreviewMode('desktop')}
                  >
                    Desktop
                  </button>
                  <button
                    type="button"
                    className={`fb-segmented-btn${previewMode === 'mobile' ? ' active' : ''}`}
                    onClick={() => setPreviewMode('mobile')}
                  >
                    Mobile
                  </button>
                </div>
                <button
                  type="button"
                  className="inbox-pill-btn fb-modal-close"
                  onClick={() => setShowPreview(false)}
                >
                  Close
                </button>
              </div>
            </div>

            <div
              className="fb-preview-frame"
              style={{
                background: theme.backgroundColor,
                borderColor: theme.borderColor,
                borderRadius: theme.borderRadius,
              }}
            >
              <h3 style={{ marginTop: 0, color: theme.textColor }}>{formName}</h3>
              
              {fields.length === 0 ? (
                <p style={{ color: theme.textColor, textAlign: 'center', padding: '2rem' }}>
                  No fields yet. Add fields to see preview.
                </p>
              ) : (
                <FormRenderer
                  fields={fields}
                  rows={rows}
                  theme={theme}
                  layout={layout}
                  layoutConfig={layoutConfig}
                  fieldDirection={designSettings.fieldDirection}
                  formName={formName}
                  formData={previewData}
                  onFieldChange={(name, value) => {
                    setPreviewData({ ...previewData, [name]: value });
                  }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    showToast('Preview mode, form not submitted', 'info');
                  }}
                  readOnly={false}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Embed Settings Modal */}
      {showEmbedSettings && formId && (
        <EmbedSettings
          formId={formId}
          embedSettings={embedSettings}
          onSave={async (settings) => {
            setEmbedSettings(settings);
            if (formId) {
              try {
                await updateDoc(doc(db, 'forms', formId), stripUndefined({ embedSettings: settings }));
              } catch (e) {
                console.error('[FormBuilder] Failed to save embed settings:', e);
              }
            }
            setShowEmbedSettings(false);
          }}
          onClose={() => setShowEmbedSettings(false)}
        />
      )}
    </div>
  );
}
